import { useArticleView } from 'providers/ArticleViewProvider/ArticleViewProvider'
import { ArticleFlat, ArticleView, Layer } from 'types'
import { Redirect, useLocation } from 'react-router-dom'
import { ARTICLE_VIEW_PARAM } from 'components/ArticleViewSelect/types'
import { PropsWithChildren } from 'react'
import { useQueryParams } from 'hooks/useQueryParams'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { PermittedArticleViewProvider } from 'providers/PermittedArticleViewProvider/PermittedArticleViewProvider'

interface Props {
  article: ArticleFlat
  layer: Layer
}

const ArticleViewRedirect = ({ children, articleView }: PropsWithChildren<{ articleView: ArticleView | null }>) => {
  const { hash, pathname, search, state } = useLocation()
  const targetArticleView = useArticleView()

  if (
    targetArticleView &&
    ((articleView && targetArticleView !== articleView) || (!articleView && targetArticleView !== ArticleView.Reading))
  ) {
    // the user does not have access to the current article view, redirect them
    const urlSearchParams = new URLSearchParams(search)

    urlSearchParams.set(ARTICLE_VIEW_PARAM, targetArticleView)

    return (
      <Redirect
        to={{
          pathname,
          hash,
          search: urlSearchParams.toString(),
          state,
        }}
      />
    )
  }

  return <>{children}</>
}

const ArticleViewRedirectContainer = ({ article, layer, children }: PropsWithChildren<Props>) => {
  const { docView } = useQueryParams()
  const articleView = useArticleView()
  let targetArticleView = articleView
  const { isMobile } = useResponsiveDesign()

  // set default view
  if (!docView || !Object.values(ArticleView).includes(docView)) {
    if (article.made_public) {
      targetArticleView = ArticleView.Reading
    } else {
      targetArticleView = isMobile ? ArticleView.Commenting : ArticleView.Editing
    }
  }

  if (!targetArticleView) {
    return <>{children}</>
  }

  return (
    <PermittedArticleViewProvider targetArticleView={targetArticleView} permissions={layer.permissions}>
      <ArticleViewRedirect articleView={articleView}>{children}</ArticleViewRedirect>
    </PermittedArticleViewProvider>
  )
}

export { ArticleViewRedirectContainer as ArticleViewRedirect }
